import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  UsersIcon,
  ClipboardDocumentCheckIcon,
  WrenchScrewdriverIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  BuildingLibraryIcon,
  BuildingOfficeIcon,
  QueueListIcon,
  ArchiveBoxIcon,
  UserGroupIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const Sidebar = ({ sidebarOpen, toggleSidebar }) => {
  const { t, isRTL } = useLanguage();
  const location = useLocation();

  const menuItems = [
    {
      name: t('dashboard'),
      href: '/dashboard',
      icon: HomeIcon,
      current: location.pathname === '/dashboard',
    },
    {
      name: t('members'),
      href: '/members',
      icon: UsersIcon,
      current: location.pathname.startsWith('/members'),
    },
    {
      name: t('subscriptions'),
      href: '/subscriptions',
      icon: ClipboardDocumentCheckIcon,
      current: location.pathname.startsWith('/subscriptions'),
    },
    {
      name: t('equipment'),
      href: '/equipment',
      icon: WrenchScrewdriverIcon,
      current: location.pathname.startsWith('/equipment'),
    },
    {
      name: t('payments'),
      href: '/payments',
      icon: CreditCardIcon,
      current: location.pathname.startsWith('/payments'),
    },
    {
      name: t('sessions'),
      href: '/sessions',
      icon: CalendarDaysIcon,
      current: location.pathname.startsWith('/sessions'),
    },
    {
      name: 'Stock',
      href: '/stock',
      icon: ArchiveBoxIcon,
      current: location.pathname.startsWith('/stock'),
    },
    {
      name: 'Employés',
      href: '/employees',
      icon: UserGroupIcon,
      current: location.pathname.startsWith('/employees'),
    },
    {
      name: 'Facturation',
      href: '/invoicing',
      icon: DocumentTextIcon,
      current: location.pathname.startsWith('/invoicing'),
    },
    {
      name: 'Bénéfices',
      href: '/benefits',
      icon: ChartBarIcon,
      current: location.pathname.startsWith('/benefits'),
    },
    {
      name: t('settings'),
      href: '/settings',
      icon: Cog6ToothIcon,
      current: location.pathname.startsWith('/settings'),
    },
  ];

  const closeSidebar = () => {
    if (window.innerWidth < 1024) {
      toggleSidebar(false);
    }
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col w-64 bg-white/5 backdrop-blur-xl border-r border-white/10 fixed inset-y-0 z-10 transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="flex-shrink-0 pt-5 pb-4 overflow-hidden">
            <div className="flex items-center flex-shrink-0 px-4">
              <img
                src="/gymfit-logo.png"
                alt="GYMIT"
                className="h-14 w-auto max-w-[210px] object-contain"
              />
            </div>
          </div>

          <nav className={`mt-8 flex-1 px-2 overflow-y-auto scrollbar-hide`}>
            <div className="space-y-1">
              {menuItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={closeSidebar}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    item.current
                      ? 'bg-white/10 text-white'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <item.icon
                    className="mr-3 flex-shrink-0 h-6 w-6"
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => toggleSidebar(false)}
        ></motion.div>
      )}

      <motion.aside
        initial={{ x: '-100%' }}
        animate={{ x: sidebarOpen ? 0 : '-100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
        className="lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white/5 backdrop-blur-xl border-r border-white/10 shadow-lg"
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between px-4 py-5">
            <div className="flex items-center">
              <img
                src="/gymfit-logo.png"
                alt="GYMIT"
                className="h-12 w-auto max-w-[180px] object-contain"
              />
            </div>
            <button
              onClick={() => toggleSidebar(false)}
              className="p-2 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <nav className="mt-8 flex-1 px-2 overflow-y-auto scrollbar-hide">
            <div className="space-y-1">
              {menuItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={closeSidebar}
                  className={`group flex items-center ${
                    isRTL ? 'pr-2 pl-3' : 'pl-2 pr-3'
                  } py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    item.current
                      ? 'bg-white/10 text-white'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <item.icon
                    className={`flex-shrink-0 h-6 w-6 ${
                      isRTL ? 'ml-3' : 'mr-3'
                    }`}
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar; 